<script setup lang="ts">
import EmblaCarousel from 'embla-carousel';
import EmblaAutoplay from 'embla-carousel-autoplay';
import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import type { AutoplayType } from 'embla-carousel-autoplay';
import EmblaDots from '@/shared/ui/EmblaCarousel/ui/EmblaDots.vue';
import EmblaButtons from '@/shared/ui/EmblaCarousel/ui/EmblaButtons.vue';
import type {
  IEmblaCarouselProps,
  IEmblaCarouselEmits,
  ArrowDirection,
} from '@/shared/ui/EmblaCarousel/model/types/emblaCarousel';

const props = withDefaults(defineProps<IEmblaCarouselProps>(), {
  options: () => ({}),
  showDots: true,
  showArrows: true,
  isAutoPlay: false,
  autoPlayDelay: 5000,
  isMobile: false,
  dotsCount: 0,
  isStopOnMouseEnterAutoPlay: false,
  autoDestroy: true,
});

const emit = defineEmits<IEmblaCarouselEmits>();

const container: Ref<HTMLElement | null> = ref(null);
const dots: Ref<InstanceType<typeof EmblaDots> | null> = ref(null);

const embla: Ref<EmblaCarouselType | null> = ref(null);
const autoplay: Ref<AutoplayType | null> = ref(null);
const scrollSnaps: Ref<number[]> = ref([]);
const selectedScrollSnap: Ref<number> = ref(0);
const prevBtnEnabled: Ref<boolean> = ref(false);
const nextBtnEnabled: Ref<boolean> = ref(true);

const localOptions: EmblaOptionsType = {
  align: 'start',
  skipSnaps: false,
};

const allOptions = computed<EmblaOptionsType>(() => ({
  ...localOptions,
  ...props.options,
}));

const onSelect = (): void => {
  if (!embla.value) return;
  selectedScrollSnap.value = embla.value.selectedScrollSnap();
  prevBtnEnabled.value = embla.value.canScrollPrev();
  nextBtnEnabled.value = embla.value.canScrollNext();
};

const initPlugins = (): void => {
  if (props.isAutoPlay) {
    autoplay.value = EmblaAutoplay({
      delay: props.autoPlayDelay,
      stopOnMouseEnter: props.isStopOnMouseEnterAutoPlay,
    });
  }
};

const onInitEmbla = (): void => {
  if (!container.value) return;
  const plugins = autoplay.value ? [autoplay.value] : [];

  embla.value = EmblaCarousel(container.value, allOptions.value, plugins);
  embla.value.on('select', onSelect);
  scrollSnaps.value = embla.value.scrollSnapList();
  emit('on-init', embla.value);
};

const handlerClickArrowButton = (type: ArrowDirection): void => {
  if (autoplay.value) autoplay.value.stop();
  if (type === 'next') embla.value?.scrollNext();
  else embla.value?.scrollPrev();
};

const handlerClickDotButton = (index: number): void => {
  embla.value?.scrollTo(index);
};

onMounted(() => {
  nextTick(() => {
    initPlugins();
    onInitEmbla();
    onSelect();
  });
});

onBeforeUnmount(() => {
  if (embla.value && props.autoDestroy) {
    embla.value.destroy();
  }
});
</script>

<template>
  <div :class="['embla', { _mobile: isMobile }]">
    <div ref="container" class="embla embla__viewport">
      <div class="embla__container">
        <slot></slot>
      </div>
    </div>
    <ClientOnly>
      <EmblaButtons
        v-if="showArrows && embla"
        :is-disabled="!prevBtnEnabled"
        @click="handlerClickArrowButton"
      />
      <EmblaButtons
        v-if="showArrows && embla"
        :is-disabled="!nextBtnEnabled"
        :is-next="true"
        @click="handlerClickArrowButton"
      />
      <div class="embla__dots">
        <EmblaDots
          v-if="showDots && embla && dotsCount > 1"
          ref="dots"
          :length-dots="dotsCount"
          :selected-item-index="selectedScrollSnap"
          :is-mobile="isMobile"
          @click="handlerClickDotButton"
        />
      </div>
    </ClientOnly>
  </div>
</template>

<style lang="less">
.embla {
  position: relative;

  &__viewport {
    overflow: hidden;

    &._is-draggable {
      cursor: grab;
    }

    &._is-dragging {
      cursor: grabbing;
    }
  }

  &__container {
    display: flex;
    will-change: transform;
  }

  &__dots {
    position: absolute;
    transform: translate(-50%);
    left: 50%;
    bottom: 0;
  }
}
</style>
